import apiClient from '../api/apiClient';

/**
 * Attribute Catalog Service
 * Handles all API calls for Pre-IPO Attribute Catalog (CRUD)
 */
class AttributeCatalogService {
  /**
   * Get attributes from catalog
   * @param {Object} params - { type: 'PRE_IPO', category?: string }
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  async getAttributes(params = {}) {
    const defaultParams = { type: 'PRE_IPO', ...params };
    const response = await apiClient.get('/api/v1/admin/attribute-catalog', {
      params: defaultParams,
    });
    const data = response.data;
    // API returns message as array (not data); normalize to data
    const list = data?.message ?? data?.data ?? [];
    return {
      success: data?.success !== false,
      data: Array.isArray(list) ? list : [],
      message: data?.message,
    };
  }

  /**
   * Get available data types for attribute creation
   * @returns {Promise<{success: boolean, data: Array<{value, label}>}>}
   */
  async getDataTypes() {
    const response = await apiClient.get('/api/v1/admin/attribute-catalog/data-types');
    const data = response.data;
    const list = data?.message ?? data?.data ?? [];
    return {
      success: data?.success !== false,
      data: Array.isArray(list) ? list : [],
    };
  }

  /**
   * Create a new attribute
   * @param {Object} payload - { product_type_id, key, label, data_type, category, validation? }
   * @returns {Promise<{success: boolean, message?: string}>}
   */
  async createAttribute(payload) {
    const response = await apiClient.post('/api/v1/admin/attribute-catalog', payload);
    return response.data;
  }

  /**
   * Update an attribute (label and other editable fields; key/data_type immutable)
   * @param {string|number} id - Attribute ID
   * @param {Object} payload - { label, ... }
   * @returns {Promise<{success: boolean, message?: string}>}
   */
  async updateAttribute(id, payload) {
    const response = await apiClient.put(`/api/v1/admin/attribute-catalog/${id}`, payload);
    return response.data;
  }

  /**
   * Delete an attribute
   * @param {string|number} id - Attribute ID
   * @returns {Promise<{success: boolean, message?: string}>}
   */
  async deleteAttribute(id) {
    const response = await apiClient.delete(`/api/v1/admin/attribute-catalog/${id}`);
    return response.data;
  }

  /**
   * Bulk upsert attributes for one category (product attribute mapping - create/update in one call per category)
   * @param {Object} payload - { product_type_id: string, product_id?: string, category: string, attributes: Array<{ id?, key_name, value, order_index, category, is_required?, data_type? }> }
   * @returns {Promise<{success: boolean, message?: string}>}
   */
  async bulkUpsertAttributes(payload) {
    const response = await apiClient.put('/api/v1/admin/attribute-catalog/bulk', payload);
    return response.data;
  }
}

const attributeCatalogService = new AttributeCatalogService();
export default attributeCatalogService;
